/** Iconos de línea, trazo 1.6, sin dependencias externas. */
import type { SVGProps } from 'react'

type P = SVGProps<SVGSVGElement> & { size?: number }

/*
 * Un solo wrapper con la geometría común: 24×24, sin relleno, trazo de 1.6 y
 * remates redondeados. Cada icono aporta únicamente su trazado, así que la
 * familia entera se ve como una familia y pesa lo que pesa un componente.
 */
function Svg({ size = 16, children, ...rest }: P) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const IconoCatalogo = (p: P) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </Svg>
)

export const IconoCarrito = (p: P) => (
  <Svg {...p}>
    <path d="M2 3h2.2l2.3 12.2h11.3" />
    <path d="M6 6.5h15l-1.7 7.2H7.2" />
    <circle cx="9" cy="19.5" r="1.4" />
    <circle cx="18" cy="19.5" r="1.4" />
  </Svg>
)

export const IconoOrden = (p: P) => (
  <Svg {...p}>
    <path d="M6 2.5h8l4 4v15H6z" />
    <path d="M14 2.5v4h4" />
    <path d="M9 12h6M9 16h6" />
  </Svg>
)

export const IconoFabrica = (p: P) => (
  <Svg {...p}>
    <path d="M3 21V9l6 3.5V9l6 3.5V6l6 3v12z" />
    <path d="M7 17h2M13 17h2M18 17h1" />
  </Svg>
)

export const IconoPanel = (p: P) => (
  <Svg {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
    <circle cx="9" cy="6" r="1.8" />
    <circle cx="15" cy="12" r="1.8" />
    <circle cx="7" cy="18" r="1.8" />
  </Svg>
)

export const IconoCamion = (p: P) => (
  <Svg {...p}>
    <path d="M2 6.5h11v10H2z" />
    <path d="M13 10h4.5l3.5 3.2v3.3H13z" />
    <circle cx="6.5" cy="18.5" r="1.6" />
    <circle cx="17" cy="18.5" r="1.6" />
  </Svg>
)

export const IconoCaja = (p: P) => (
  <Svg {...p}>
    <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" />
    <path d="M3 7.5 12 12l9-4.5M12 12v9" />
  </Svg>
)

export const IconoPallet = (p: P) => (
  <Svg {...p}>
    <path d="M6 4h12v7H6z" />
    <path d="M3 15h18M3 15v4M9 15v4M15 15v4M21 15v4M3 19h18" />
  </Svg>
)

export const IconoPin = (p: P) => (
  <Svg {...p}>
    <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
)

export const IconoBuscar = (p: P) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 4.5 4.5" />
  </Svg>
)

export const IconoCheck = (p: P) => (
  <Svg {...p}>
    <path d="m4 12.5 5 5L20 6.5" />
  </Svg>
)

export const IconoEscudo = (p: P) => (
  <Svg {...p}>
    <path d="M12 2.8 20 6v6c0 5-3.4 8-8 9.2C7.4 20 4 17 4 12V6z" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </Svg>
)

export const IconoAlerta = (p: P) => (
  <Svg {...p}>
    <path d="M12 3.5 22 20H2z" />
    <path d="M12 10v4.5M12 17.2v.1" />
  </Svg>
)

export const IconoBasura = (p: P) => (
  <Svg {...p}>
    <path d="M4 6.5h16M9.5 6.5V4h5v2.5" />
    <path d="M6.5 6.5 7.5 21h9l1-14.5" />
  </Svg>
)

export const IconoFlecha = (p: P) => (
  <Svg {...p}>
    <path d="m9 5 7 7-7 7" />
  </Svg>
)

/** La campana de novedades. */
export const IconoCampana = (p: P) => (
  <Svg {...p}>
    <path d="M18 15V10a6 6 0 1 0-12 0v5l-1.5 2.5h15L18 15Z" />
    <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
  </Svg>
)

/** Hoja con la esquina doblada: la ficha técnica, el certificado, el manual. */
export const IconoDocumento = (p: P) => (
  <Svg {...p}>
    <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z" />
    <path d="M14 3v4h4" />
    <path d="M9 13h6M9 16.5h4" />
  </Svg>
)

/** Ojo: ver el documento sin bajarlo, que es lo que hace el enlace. */
export const IconoVer = (p: P) => (
  <Svg {...p}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
)

export const IconoMenos = (p: P) => (
  <Svg {...p}>
    <path d="M5 12h14" />
  </Svg>
)

export const IconoMas = (p: P) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

export const IconoBalanza = (p: P) => (
  <Svg {...p}>
    <path d="M12 4v16M7 20h10" />
    <path d="M4 9h16M4 9l-2 5h4zM20 9l2 5h-4z" />
  </Svg>
)

export const IconoRegla = (p: P) => (
  <Svg {...p}>
    <path d="M3 15 15 3l6 6L9 21z" />
    <path d="m7 11 2 2M10 8l2 2M13 5l2 2" />
  </Svg>
)

/** Icono por categoría, para el tile de producto. */
export function iconoCategoria(categoriaId: string) {
  switch (categoriaId) {
    case 'epp':
      return IconoEscudo
    case 'izaje':
      return IconoBalanza
    case 'iluminacion':
      return IconoPanel
    case 'lubricantes':
    case 'filtros':
      return IconoPallet
    case 'senaletica':
      return IconoAlerta
    default:
      return IconoCaja
  }
}
